/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Tooltip,
  CircularProgress,
  Button,
  LinearProgress,
  Stack,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Iconify from '../Iconify';
import DentallyAvailabilityResults from './DentallyAvailabilityResults';
import { fetchDentallyAvailability } from '../../utils/dentallyApi';
import { availPractitionerName, parseDentallyDate } from '../../utils/dentallyUtils';
import { useSelector } from '../../redux/store';

const TIMEFRAME_PRESETS = [
  { key: '1day', label: 'Next 1 Day', days: 1 },
  { key: '2days', label: 'Next 2 Days', days: 2 },
  { key: '7days', label: 'Next 7 Days', days: 7 },
  { key: '14days', label: 'Next 14 Days', days: 14 },
  { key: 'custom', label: 'Custom', days: null },
];

const DURATION_OPTIONS = [10, 15, 20, 30, 45, 60, 75, 90, 110, 120];

// Split a large availability window into discrete time slots of the requested duration.
// Uses parseDentallyDate to handle DD-MM-YYYY format from the Dentally API.
const splitSlotsByDuration = (slots, durationMinutes) => {
  if (!durationMinutes || durationMinutes <= 0) return slots;
  const now = Date.now();
  const result = [];
  slots.forEach((slot) => {
    const startDate = parseDentallyDate(slot.start_time);
    const endDate = parseDentallyDate(slot.finish_time);
    if (!startDate || !endDate) return; // skip unparseable slots
    const start = startDate.getTime();
    const end = endDate.getTime();
    if (end <= now) return; // skip past slots entirely
    const windowMinutes = (end - start) / 60000;
    if (windowMinutes <= durationMinutes) {
      // Normalize to ISO so downstream code always sees ISO format
      result.push({ ...slot, start_time: startDate.toISOString(), finish_time: endDate.toISOString() });
      return;
    }
    const step = durationMinutes * 60000;
    let cursor = Math.max(start, now); // skip sub-slots in the past
    // Align cursor to the next slot boundary from the original start
    if (cursor > start) {
      const elapsed = cursor - start;
      cursor = start + Math.ceil(elapsed / step) * step;
    }
    while (cursor + step <= end) {
      result.push({
        ...slot,
        start_time: new Date(cursor).toISOString(),
        finish_time: new Date(cursor + step).toISOString(),
      });
      cursor += step;
    }
  });
  return result;
};

/**
 * Inline (non-drawer) availability slot picker for use as a wizard step.
 * Renders the practitioner selector, timeframe/duration presets, and slot grid
 * without any Drawer chrome.
 *
 * Props:
 *   practiceId  — required
 *   selectedSlot — currently selected slot (controlled, from parent state)
 *   onSelect    — called with slot object when slot is selected, or null to clear
 */
export default function DentallyAvailabilityStep({
  practiceId,
  selectedSlot,
  onSelect,
  assignToState,
  onAssignToChange,
  availSearchState,
  onAvailSearchStateChange,
  hideUserSelection,
  compact,
}) {
  const theme = useTheme(); // eslint-disable-line no-unused-vars

  const { practitioners: allPractitioners, isLoading: practLoading } = useSelector(
    (state) => state.dentallyPractitioners
  );
  const { practiceUser } = useSelector((state) => state.practiceUser);
  const { practice: practiceData } = useSelector((state) => state.practice);

  const assignableUsers = useMemo(() => {
    const results = practiceUser?.results || [];
    return results.filter((pu) => {
      const role = pu.role?.toUpperCase();
      if (role === 'ADMIN' || role === 'SUPPORT') return false;
      if (pu.user?.is_superuser) return false;
      return true;
    });
  }, [practiceUser]);

  const activePractitioners = useMemo(() => {
    const seen = new Set();
    return (allPractitioners || []).filter((p) => {
      if (p.active === false || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [allPractitioners]);

  // Use lifted state from parent if provided, otherwise fall back to local state
  const [localPractitioners, setLocalPractitioners] = useState([]);
  const selectedPractitioners = availSearchState?.selectedPractitioners ?? localPractitioners;
  const setSelectedPractitioners = useCallback(
    (val) => {
      const next = typeof val === 'function' ? val(selectedPractitioners) : val;
      setLocalPractitioners(next);
      onAvailSearchStateChange?.((prev) => ({ ...prev, selectedPractitioners: next }));
    },
    [selectedPractitioners, onAvailSearchStateChange]
  );

  const allSelected = activePractitioners.length > 0 && selectedPractitioners.length === activePractitioners.length;

  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedPractitioners([]);
    } else {
      setSelectedPractitioners([...activePractitioners]);
    }
  };

  const [practSearch, setPractSearch] = useState('');
  const [showAllPract, setShowAllPract] = useState(false);
  const MAX_VISIBLE_PRACTITIONERS = 6;

  const [localTimeframe, setLocalTimeframe] = useState('7days');
  const timeframePreset = availSearchState?.timeframePreset ?? localTimeframe;
  const setTimeframePreset = useCallback(
    (val) => {
      setLocalTimeframe(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, timeframePreset: val }));
    },
    [onAvailSearchStateChange]
  );

  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  const [localDuration, setLocalDuration] = useState(30);
  const duration = availSearchState?.duration ?? localDuration;
  const setDuration = useCallback(
    (val) => {
      setLocalDuration(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, duration: val }));
    },
    [onAvailSearchStateChange]
  );

  const [localResults, setLocalResults] = useState([]);
  const results = availSearchState?.results ?? localResults;
  const setResults = useCallback(
    (val) => {
      const next = typeof val === 'function' ? val(results) : val;
      setLocalResults(next);
      onAvailSearchStateChange?.((prev) => ({ ...prev, results: next }));
    },
    [results, onAvailSearchStateChange]
  );

  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });

  const [localHasSearched, setLocalHasSearched] = useState(false);
  const hasSearched = availSearchState?.hasSearched ?? localHasSearched;
  const setHasSearched = useCallback(
    (val) => {
      setLocalHasSearched(val);
      onAvailSearchStateChange?.((prev) => ({ ...prev, hasSearched: val }));
    },
    [onAvailSearchStateChange]
  );

  const getTimeRange = useCallback(() => {
    if (timeframePreset === 'custom') {
      return {
        start: customStartDate ? customStartDate.toISOString() : new Date().toISOString(),
        end: customEndDate ? customEndDate.toISOString() : new Date().toISOString(),
      };
    }
    const preset = TIMEFRAME_PRESETS.find((p) => p.key === timeframePreset);
    const now = new Date();
    const end = new Date(now);
    end.setDate(end.getDate() + (preset?.days || 7));
    return { start: now.toISOString(), end: end.toISOString() };
  }, [timeframePreset, customStartDate, customEndDate]);

  const handleSearch = useCallback(async () => {
    if (!practiceId || selectedPractitioners.length === 0) return;
    const effectiveDuration = typeof duration === 'number' && duration > 0 ? duration : 30;
    const { start, end } = getTimeRange();
    setIsSearching(true);
    setHasSearched(true);
    setResults([]);
    onSelect(null); // clear any previously selected slot

    const total = selectedPractitioners.length;
    setSearchProgress({ done: 0, total });

    const nameMap = {};
    selectedPractitioners.forEach((p) => {
      nameMap[p.id] = availPractitionerName(p);
    });

    try {
      const promises = selectedPractitioners.map((pract) =>
        fetchDentallyAvailability(practiceId, {
          startTime: start,
          finishTime: end,
          practitionerIds: [pract.id],
          duration: effectiveDuration,
          perPage: 50,
          page: 1,
        })
          .then((res) => {
            setSearchProgress((prev) => ({ done: prev.done + 1, total: prev.total }));
            const slots = res?.ok || res?.status === 200 ? res.data?.availability || [] : [];
            return (Array.isArray(slots) ? slots : []).map((s) => ({
              ...s,
              _queried_practitioner_id: pract.id,
              _queried_practitioner_name: nameMap[pract.id] || availPractitionerName(pract),
            }));
          })
          .catch(() => {
            setSearchProgress((prev) => ({ done: prev.done + 1, total: prev.total }));
            return [];
          })
      );

      const merged = (await Promise.all(promises)).flat();
      const sliced = splitSlotsByDuration(merged, effectiveDuration);
      sliced.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
      setResults(sliced);
    } catch {
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [practiceId, selectedPractitioners, getTimeRange, duration, onSelect]);

  const canSearch =
    selectedPractitioners.length > 0 && (timeframePreset !== 'custom' || (customStartDate && customEndDate));

  // DEV-only: auto-select "Dr Raj Gohil" practitioner + trigger search (Test Practice only)
  const isTestPractice = practiceData?.name?.toLowerCase().includes('test practice');
  const devAutoSearched = useRef(false);
  useEffect(() => {
    if (
      !import.meta.env.DEV ||
      !isTestPractice ||
      devAutoSearched.current ||
      selectedSlot ||
      practLoading ||
      !activePractitioners.length
    )
      return;
    const rajGohil = activePractitioners.find((p) => {
      const u = p.user || {};
      const fullName = [u.title, u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
      return fullName.includes('raj') && fullName.includes('gohil');
    });
    if (rajGohil) {
      setSelectedPractitioners([rajGohil]);
      devAutoSearched.current = true;
    }
  }, [activePractitioners, practLoading, isTestPractice]);

  // DEV-only: auto-trigger search once practitioner is selected
  useEffect(() => {
    if (
      !import.meta.env.DEV ||
      !isTestPractice ||
      !devAutoSearched.current ||
      selectedPractitioners.length === 0 ||
      isSearching ||
      hasSearched
    )
      return;
    handleSearch();
  }, [selectedPractitioners, isTestPractice]);

  // DEV-only: auto-select first available slot when results arrive
  useEffect(() => {
    if (!import.meta.env.DEV || !isTestPractice || !devAutoSearched.current || selectedSlot || results.length === 0)
      return;
    onSelect(results[0]);
  }, [results, isTestPractice]);

  const progressPercent = searchProgress.total > 0 ? Math.round((searchProgress.done / searchProgress.total) * 100) : 0;

  return (
    <Box>
      {/* ── 👥 TEAM SECTION ── */}
      {!hideUserSelection && (
        <Box sx={{ mb: 2.5 }}>
          {!compact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#00AB55',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  fontWeight: 700,
                }}
              >
                2
              </Box>
              <Iconify icon="eva:people-fill" width={16} sx={{ color: '#637381' }} />
              <Typography
                sx={{
                  fontSize: '11px',
                  color: '#637381',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: 700,
                }}
              >
                Team
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              ...(!compact && { background: '#f8f9fa', borderRadius: '12px', p: 2 }),
              display: 'flex',
              gap: compact ? 2 : 4,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                Booked with
              </Typography>
              {practLoading ? (
                <CircularProgress size={16} />
              ) : (
                <>
                  {/* Search — only when more than MAX_VISIBLE */}
                  {activePractitioners.length > MAX_VISIBLE_PRACTITIONERS && (
                    <TextField
                      size="small"
                      fullWidth
                      value={practSearch}
                      onChange={(e) => {
                        setPractSearch(e.target.value);
                        if (e.target.value) setShowAllPract(true);
                      }}
                      placeholder="Search practitioners…"
                      sx={{
                        mb: 0.75,
                        '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: 28 },
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(145,158,171,0.16)' },
                      }}
                    />
                  )}
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(() => {
                      const q = practSearch.toLowerCase();
                      const filtered = q
                        ? activePractitioners.filter((p) => availPractitionerName(p).toLowerCase().includes(q))
                        : activePractitioners;
                      const visible = showAllPract ? filtered : filtered.slice(0, MAX_VISIBLE_PRACTITIONERS);
                      const overflow = filtered.length - MAX_VISIBLE_PRACTITIONERS;
                      return (
                        <>
                          {visible.map((p) => {
                            const u = p.user || {};
                            const initials =
                              ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?';
                            const name = availPractitionerName(p);
                            const isSelected = selectedPractitioners.some((sp) => sp.id === p.id);
                            const colour = p.colour || '#078DEE';
                            return (
                              <Tooltip
                                key={p.id}
                                title={name}
                                arrow
                                placement="top"
                                componentsProps={{
                                  tooltip: { sx: { bgcolor: '#078DEE', fontSize: '11px', fontWeight: 600 } },
                                  arrow: { sx: { color: '#078DEE' } },
                                }}
                              >
                                <Box
                                  onClick={() => {
                                    if (isSelected)
                                      setSelectedPractitioners((prev) => prev.filter((sp) => sp.id !== p.id));
                                    else setSelectedPractitioners((prev) => [...prev, p]);
                                  }}
                                  sx={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: isSelected ? '#00AB55' : 'transparent',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: isSelected ? 'rgba(0,171,85,0.06)' : `${colour}14`,
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: isSelected ? '#00AB55' : colour,
                                    transition: 'all 150ms ease',
                                    '&:hover': { borderColor: colour },
                                  }}
                                >
                                  {initials}
                                </Box>
                              </Tooltip>
                            );
                          })}
                          {!showAllPract && overflow > 0 && !q && (
                            <Tooltip title={`Show ${overflow} more`} arrow placement="top">
                              <Box
                                onClick={() => setShowAllPract(true)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  borderRadius: '50%',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: '1.5px dashed',
                                  borderColor: '#C4CDD5',
                                  color: '#919EAB',
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  transition: 'all 150ms ease',
                                  '&:hover': { borderColor: '#078DEE', color: '#078DEE' },
                                }}
                              >
                                +{overflow}
                              </Box>
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                    {activePractitioners.length > 0 && (
                      <Tooltip title={allSelected ? 'Deselect all' : 'Select all'} arrow placement="top">
                        <Box
                          onClick={handleToggleSelectAll}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1.5px dashed',
                            borderColor: allSelected ? '#00AB55' : '#C4CDD5',
                            color: allSelected ? '#00AB55' : '#919EAB',
                            fontSize: '9px',
                            fontWeight: 700,
                            transition: 'all 150ms ease',
                            '&:hover': { borderColor: '#00AB55', color: '#00AB55' },
                          }}
                        >
                          {allSelected ? <Iconify icon="mdi:check-all" width={14} /> : 'ALL'}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </>
              )}
            </Box>
            {assignableUsers.length > 0 && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '10px',
                    color: '#919EAB',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px',
                    fontWeight: 600,
                    mb: 0.75,
                  }}
                >
                  Assigned to
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {assignableUsers.map((pu) => {
                    const u = pu.user || {};
                    const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ') || 'User';
                    const initials = ((u.first_name?.[0] || '') + (u.last_name?.[0] || '')).toUpperCase() || '?';
                    const isSelected = (assignToState || []).some((a) => a.id === pu.id);
                    return (
                      <Tooltip
                        key={pu.id}
                        title={fullName}
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: { sx: { bgcolor: '#855CF8', fontSize: '11px', fontWeight: 600 } },
                          arrow: { sx: { color: '#855CF8' } },
                        }}
                      >
                        <Box
                          onClick={() => {
                            if (!onAssignToChange) return;
                            if (isSelected) onAssignToChange((assignToState || []).filter((a) => a.id !== pu.id));
                            else onAssignToChange([...(assignToState || []), pu]);
                          }}
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            border: '2px solid',
                            borderColor: isSelected ? '#00AB55' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isSelected ? 'rgba(0,171,85,0.06)' : 'rgba(133,92,248,0.08)',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: isSelected ? '#00AB55' : '#855CF8',
                            transition: 'all 150ms ease',
                            '&:hover': { borderColor: '#855CF8' },
                          }}
                        >
                          {initials}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── 📅 AVAILABILITY SECTION ── */}
      <Box sx={{ mb: 2 }}>
        {!compact && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: '#00AB55',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                fontWeight: 700,
              }}
            >
              3
            </Box>
            <Iconify icon="eva:calendar-fill" width={16} sx={{ color: '#637381' }} />
            <Typography
              sx={{
                fontSize: '11px',
                color: '#637381',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 700,
              }}
            >
              Availability
            </Typography>
          </Box>
        )}
        <Box sx={{ ...(!compact && { background: '#f8f9fa', borderRadius: '12px', p: 2 }) }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: compact ? 1 : 2,
              mb: 1.5,
              flexDirection: compact ? 'column' : 'row',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Timeframe
              </Typography>
              {/* eslint-disable no-nested-ternary */}
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {TIMEFRAME_PRESETS.map(({ key, label }) => (
                  <Box
                    key={key}
                    onClick={() => setTimeframePreset(key)}
                    sx={{
                      px: compact ? 0.75 : 1.25,
                      py: compact ? 0.25 : 0.375,
                      borderRadius: compact ? '6px' : '14px',
                      fontSize: compact ? '10px' : '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: timeframePreset === key ? '#00AB55' : compact ? 'transparent' : '#e0e0e0',
                      color: timeframePreset === key ? '#00AB55' : '#919EAB',
                      background: timeframePreset === key ? 'rgba(0,171,85,0.06)' : compact ? '#f4f6f8' : 'transparent',
                      transition: 'all 150ms ease',
                      whiteSpace: 'nowrap',
                      '&:hover': { color: '#00AB55', background: 'rgba(0,171,85,0.06)' },
                    }}
                  >
                    {compact ? label.replace('Next ', '') : label}
                  </Box>
                ))}
              </Box>
              {/* eslint-enable no-nested-ternary */}
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '10px',
                  color: '#919EAB',
                  textTransform: 'uppercase',
                  letterSpacing: '0.3px',
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Duration
              </Typography>
              <TextField
                select
                size="small"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                sx={{
                  minWidth: compact ? 80 : 130,
                  ...(compact && {
                    '& .MuiOutlinedInput-root': { fontSize: '12px', height: 28, borderRadius: '8px' },
                    '& .MuiSelect-select': { py: '4px' },
                  }),
                }}
              >
                {DURATION_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {(() => {
                      if (compact) {
                        if (m < 60) return `${m}m`;
                        return `${Math.floor(m / 60)}h${m % 60 > 0 ? `${m % 60}` : ''}`;
                      }
                      if (m < 60) return `${m} min`;
                      if (m === 60) return '1 hour';
                      return `${Math.floor(m / 60)}h ${m % 60 > 0 ? `${m % 60}m` : ''}`.trim();
                    })()}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
          {timeframePreset === 'custom' && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
              <DateTimePicker
                label="Start"
                value={customStartDate}
                onChange={setCustomStartDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDateTime={new Date()}
              />
              <DateTimePicker
                label="End"
                value={customEndDate}
                onChange={setCustomEndDate}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                minDateTime={customStartDate || new Date()}
              />
            </Stack>
          )}

          {/* Search button */}
          <LoadingButton
            fullWidth
            variant="contained"
            loading={isSearching}
            onClick={handleSearch}
            disabled={!canSearch}
            startIcon={<Iconify icon="mdi:magnify" width={compact ? 14 : 18} />}
            sx={{
              mb: 1,
              py: compact ? 0.5 : 1,
              fontWeight: 600,
              fontSize: compact ? '12px' : '0.875rem',
              borderRadius: compact ? '8px' : undefined,
            }}
          >
            {isSearching ? `Searching ${searchProgress.done}/${searchProgress.total}…` : 'Search Availability'}
          </LoadingButton>

          {isSearching && searchProgress.total > 1 && (
            <LinearProgress variant="determinate" value={progressPercent} sx={{ mb: 1 }} />
          )}

          {/* Skip link */}
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Button
              size="small"
              onClick={() => onSelect({ _manual: true })}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: compact ? 11 : 12 }}
            >
              Skip — Enter Appointment Time Manually
            </Button>
          </Box>

          {/* Results */}
          {hasSearched && (
            <>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', fontSize: compact ? 10 : undefined }}
              >
                {results.length > 0 ? `${results.length} slot(s) found` : 'No slots found'}
              </Typography>
              <DentallyAvailabilityResults
                results={results}
                onSelect={onSelect}
                practitioners={allPractitioners}
                selectedSlot={selectedSlot}
                loading={isSearching}
                compact={compact}
              />
            </>
          )}

          {/* Selected slot summary */}
          {selectedSlot && !selectedSlot._manual && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'rgba(0,171,85,0.08)',
                borderRadius: 1,
                border: '1px solid rgba(0,171,85,0.3)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="mdi:check-circle" width={18} sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Slot selected
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString(
                      'en-GB',
                      {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      }
                    )}
                    {selectedSlot._queried_practitioner_name && ` · ${selectedSlot._queried_practitioner_name}`}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Manual slot indicator */}
          {selectedSlot?._manual && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'rgba(255,171,0,0.08)',
                borderRadius: 1,
                border: '1px solid rgba(255,171,0,0.3)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="mdi:pencil-outline" width={18} sx={{ color: 'warning.main' }} />
                <Typography variant="body2">
                  Manual booking — appointment time will be entered in the next step
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

DentallyAvailabilityStep.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  selectedSlot: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  assignToState: PropTypes.array,
  onAssignToChange: PropTypes.func,
  availSearchState: PropTypes.object,
  onAvailSearchStateChange: PropTypes.func,
  hideUserSelection: PropTypes.bool,
  compact: PropTypes.bool,
};

DentallyAvailabilityStep.defaultProps = {
  selectedSlot: null,
  assignToState: [],
  onAssignToChange: null,
  availSearchState: null,
  onAvailSearchStateChange: null,
  hideUserSelection: false,
  compact: false,
};
